import {
  mergeArrays,
  NA_COLOR,
  NEUTRAL_PALETTE,
  NutrientManagementDatatype,
} from "./types";

export const FERTILIZER_RECOMMENDATION_DATATYPES: NutrientManagementDatatype[] =
  [
    {
      name: "Lime",
      id: "rx_lime",
      visualizationKey: "rx_lime",
      mapboxPaintStyle: {
        "fill-color": [
          "interpolate",
          ["linear"],
          ["get", "rx_lime"],
          ...mergeArrays(
            [0, 0.0001, 0.5, 1.0, 2.0, 3.0],
            [NA_COLOR, ...NEUTRAL_PALETTE],
          ),
        ],
      },
      categorization: [
        {
          id: "none",
          label: "No lime recommended",
          valueMatch: (value) => value === 0,
          color: [NA_COLOR],
        },
        {
          id: "low",
          label: "Apply less than .5 tons/acre",
          valueMatch: (value) => value > 0 && value <= 0.5,
          color: [NEUTRAL_PALETTE[0], NEUTRAL_PALETTE[1]],
        },
        {
          id: "low-mid",
          label: "Apply .5 to 1.0 tons/acre",
          valueMatch: (value) => value > 0.5 && value <= 1.0,
          color: [NEUTRAL_PALETTE[1], NEUTRAL_PALETTE[2]],
        },
        {
          id: "mid-high",
          label: "Apply 1 to 2 tons/acre",
          valueMatch: (value) => value > 1.0 && value <= 2.0,
          color: [NEUTRAL_PALETTE[2], NEUTRAL_PALETTE[3]],
        },
        {
          id: "high",
          label: "Apply 2 to 3 tons/acre",
          valueMatch: (value) => value > 2.0 && value <= 3.0,
          color: [NEUTRAL_PALETTE[3], NEUTRAL_PALETTE[4]],
        },
      ],
      histogramTitle: "Recommended Lime application across all fields:",
      description: (
        <>
          <div>
            <span className="font-bold">Where is this data from?</span>
            <p>
              Prescription created by Vayda, based on annual nutrient testing.
            </p>
          </div>
          <p>
            Values shown on map are recommended Lime application in tons/acre.
          </p>
        </>
      ),
      partnerLayerIds: ["vendor_ph_h20", "base_sat", "vendor_cec_meq_100_g"],
    },
    {
      name: "Sulfur (S)",
      id: "rx_s",
      visualizationKey: "rx_s",
      mapboxPaintStyle: {
        "fill-color": [
          "interpolate",
          ["linear"],
          ["get", "rx_s"],
          ...mergeArrays(
            [0, 0.0001, 5, 10, 15, 20],
            [NA_COLOR, ...NEUTRAL_PALETTE],
          ),
        ],
      },
      categorization: [
        {
          id: "none",
          label: "No Sulfur recommended",
          valueMatch: (value) => value === 0,
          color: [NA_COLOR],
        },
        {
          id: "low",
          label: "Apply 0 to 5 lbs/acre",
          valueMatch: (value) => value > 0 && value <= 5,
          color: [NEUTRAL_PALETTE[0], NEUTRAL_PALETTE[1]],
        },
        {
          id: "low-mid",
          label: "Apply 5 to 10 lbs/acre",
          valueMatch: (value) => value > 5 && value <= 10,
          color: [NEUTRAL_PALETTE[1], NEUTRAL_PALETTE[2]],
        },
        {
          id: "mid",
          label: "Apply 10 to 15 lbs/acre",
          valueMatch: (value) => value > 10 && value <= 15,
          color: [NEUTRAL_PALETTE[2], NEUTRAL_PALETTE[3]],
        },
        {
          id: "mid-high",
          label: "Apply 15 to 20 lbs/acre",
          valueMatch: (value) => value > 15 && value <= 20,
          color: [NEUTRAL_PALETTE[3], NEUTRAL_PALETTE[4]],
        },
        {
          id: "high",
          label: "Apply 20 to 25 lbs/acre",
          valueMatch: (value) => value > 20,
          color: [NEUTRAL_PALETTE[4]],
        },
      ],
      histogramTitle: "Recommended Sulfur application across all fields:",
      description: (
        <>
          <div>
            <span className="font-bold">Where is this data from?</span>
            <p>
              Prescription created by Vayda, based on annual nutrient testing.
            </p>
          </div>
          <p>
            Values shown on map are recommended Sulfur application in lbs/acre.
          </p>
        </>
      ),
      partnerLayerIds: ["vender_om"],
    },
    {
      name: "Phosphorus (P₂O₅)",
      id: "rx_p2o5_general",
      visualizationKey: "rx_p2o5_msu",
      mapboxPaintStyle: {
        "fill-color": [
          "interpolate",
          ["linear"],
          ["get", "rx_p2o5_msu"],
          ...mergeArrays(
            [0, 0.0001, 20, 40, 60, 80],
            [NA_COLOR, ...NEUTRAL_PALETTE],
          ),
        ],
      },
      partnerLayerIds: ["vendor_p_ppm"],
      categorization: [
        {
          id: "none",
          label: "No Phosphorus recommended",
          valueMatch: (value) => value === 0,
          color: [NA_COLOR],
        },
        {
          id: "low",
          label: "Apply 0 to 20 lbs/acre",
          valueMatch: (value) => value > 0 && value <= 20,
          color: [NEUTRAL_PALETTE[0], NEUTRAL_PALETTE[1]],
        },
        {
          id: "low-mid",
          label: "Apply 20 to 40 lbs/acre",
          valueMatch: (value) => value > 20 && value <= 40,
          color: [NEUTRAL_PALETTE[1], NEUTRAL_PALETTE[2]],
        },
        {
          id: "mid",
          label: "Apply 40 to 60 lbs/acre",
          valueMatch: (value) => value > 40 && value <= 60,
          color: [NEUTRAL_PALETTE[2], NEUTRAL_PALETTE[3]],
        },
        {
          id: "mid-high",
          label: "Apply 60 to 80 lbs/acre",
          valueMatch: (value) => value > 60 && value <= 80,
          color: [NEUTRAL_PALETTE[3], NEUTRAL_PALETTE[4]],
        },
        {
          id: "high",
          label: "Apply 80 to 100 lbs/acre",
          valueMatch: (value) => value > 80,
          color: [NEUTRAL_PALETTE[4]],
        },
      ],
      histogramTitle:
        "Recommended Phosphorus (P₂O₅) application across all fields:",
      description: (
        <>
          <div>
            <span className="font-bold">Where is this data from?</span>
            <p>
              Prescription created by Vayda, based on annual nutrient testing.
            </p>
          </div>
          <p>
            Values shown on map are recommended Phosphorus (P₂O₅) application in
            lbs/acre.
          </p>
        </>
      ),
    },
    {
      name: "Potassium (K₂O)",
      id: "rx_k2o_general",
      visualizationKey: "rx_k2o_msu",
      mapboxPaintStyle: {
        "fill-color": [
          "interpolate",
          ["linear"],
          ["get", "rx_k2o_msu"],
          ...[
            0,
            NA_COLOR,
            0.0001,
            "#edf8fb",
            20,
            "#bfd3e6",
            40,
            "#9ebcda",
            60,
            "#8c96c6",
            80,
            "#8c6bb1",
            100,
            "#88419d",
            120,
            "#6e016b",
          ],
        ],
      },
      partnerLayerIds: ["vendor_k_ppm", "vendor_cec_meq_100_g"],
      categorization: [
        {
          id: "none",
          label: "No Potassium recommended",
          valueMatch: (value) => value === 0,
          color: [NA_COLOR],
        },
        {
          id: "low",
          label: "Apply 0 to 20 lbs/acre",
          valueMatch: (value) => value > 0 && value <= 20,
          color: ["#edf8fb", "#bfd3e6"],
        },
        {
          id: "low-mid",
          label: "Apply 20 to 40 lbs/acre",
          valueMatch: (value) => value > 20 && value <= 40,
          color: ["#bfd3e6", "#9ebcda"],
        },
        {
          id: "mid",
          label: "Apply 40 to 60 lbs/acre",
          valueMatch: (value) => value > 40 && value <= 60,
          color: ["#9ebcda", "#8c96c6"],
        },
        {
          id: "mid-high",
          label: "Apply 60 to 80 lbs/acre",
          valueMatch: (value) => value > 60 && value <= 80,
          color: ["#8c96c6", "#8c6bb1"],
        },
        {
          id: "high",
          label: "Apply 80 to 100 lbs/acre",
          valueMatch: (value) => value > 80 && value <= 100,
          color: ["#8c6bb1", "#88419d"],
        },
        {
          id: "very-high",
          label: "Apply 100 to 120 lbs/acre",
          valueMatch: (value) => value > 100 && value <= 120,
          color: ["#88419d", "#6e016b"],
        },
        {
          id: "extreme",
          label: "Apply 120 to 140 lbs/acre",
          valueMatch: (value) => value > 120,
          color: ["#6e016b"],
        },
      ],
      histogramTitle:
        "Recommended Potassium (K₂O) application across all fields:",
      description: (
        <>
          <div>
            <span className="font-bold">Where is this data from?</span>
            <p>
              Prescription created by Vayda, based on annual nutrient testing.
            </p>
          </div>
          <p>
            Values shown on map are recommended Potassium (K₂O) application in
            lbs/acre.
          </p>
        </>
      ),
    },
  ];
